import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../api/client';
import { usePrintHeader } from '../../hooks/usePrintHeader';
import { addressLine, date, money, payWords } from '../../utils/format';
import { ErrorNotice, Loading } from '../../components/ui';
import { PrintDocument, PrintToolbar } from '../../components/print/PrintDocument';

interface Advance {
  doc_no: string;
  doc_date: string;
  kind: 'ADVANCE' | 'ADVANCE_REFUND';
  direction: 'IN' | 'OUT';
  party_type: 'CUSTOMER' | 'SUPPLIER';
  party_name: string;
  party_phone: string | null;
  party_address: unknown;
  party_gstin: string | null;
  mode: string;
  amount: string;
  purpose: string | null;
  reference_no: string | null;
  narration: string | null;
  status: string;
  branch_name: string;
  entered_by: string | null;
  balance_now: string;
}

/**
 * The paper for an advance: the receipt a customer takes away for their
 * booking money, or the voucher kept for money paid ahead to a supplier — and
 * either side's refund. It says what the advance is for and what is held now.
 */
export function AdvancePrintPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ['advance', id],
    queryFn: () => api.get<Advance>(`/advances/${id}`),
  });
  const { header } = usePrintHeader(data?.branch_name);

  if (isLoading) return <Loading what="Preparing the receipt" />;
  if (error || !data) return <ErrorNotice error={error} />;

  const customer = data.party_type === 'CUSTOMER';
  const refund = data.kind === 'ADVANCE_REFUND';
  const cancelled = data.status === 'CANCELLED';
  const title = refund
    ? customer
      ? 'Advance refund voucher'
      : 'Advance returned by supplier'
    : customer
      ? 'Advance receipt'
      : 'Advance payment voucher';

  return (
    <>
      <PrintToolbar onBack={() => navigate('/advances?tab=documents')} fileName={data.doc_no} />
      <PrintDocument
        title={cancelled ? `${title} (cancelled)` : title}
        header={header}
        docNo={data.doc_no}
        docDate={date(data.doc_date)}
        party={{
          name: data.party_name,
          phone: data.party_phone,
          address: addressLine(data.party_address) || null,
          gstin: data.party_gstin,
        }}
        partyLabel={refund ? (customer ? 'Refunded to' : 'Received from') : customer ? 'Received from' : 'Paid to'}
        details={[
          { label: refund ? 'Reason' : 'For', value: data.purpose ?? '' },
          { label: data.direction === 'IN' ? 'Received by' : 'Paid by', value: payWords(data.mode) },
          { label: 'Reference', value: data.reference_no ?? '' },
          { label: 'Entered by', value: data.entered_by ?? '' },
        ]}
        totals={[]}
        grandTotal={data.amount}
        grandTotalLabel={refund ? 'Amount refunded' : customer ? 'Advance received' : 'Advance paid'}
        footNote={[
          customer
            ? refund
              ? 'This amount has been paid back out of the advance held for you.'
              : 'This is an advance, not a bill. It is held in your name and will be adjusted against your purchase. Please bring this receipt.'
            : refund
              ? 'This amount has been received back against the advance paid to you.'
              : 'Paid in advance against goods to be supplied. It will be adjusted against your bill.',
          cancelled
            ? 'This document has been cancelled.'
            : `${customer ? 'Advance held for you with the shop' : 'Advance held with you'} today: ${money(data.balance_now)}.`,
          data.narration ?? '',
        ]
          .filter(Boolean)
          .join('\n')}
        signatures={
          customer
            ? [refund ? 'Received by (customer)' : 'Customer signature', `For ${header.companyName}`]
            : ['Received by (supplier)', `For ${header.companyName}`]
        }
      >
        <table className="doc-table">
          <thead>
            <tr>
              <th>Particulars</th>
              <th className="r">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <span className="font-medium">
                  {refund ? 'Refund of advance' : customer ? 'Advance towards purchase' : 'Advance against supply'}
                </span>
                {data.purpose ? <span className="block text-[11px] text-ink-soft">{data.purpose}</span> : null}
              </td>
              <td className="num font-semibold">{money(data.amount)}</td>
            </tr>
          </tbody>
        </table>
      </PrintDocument>
    </>
  );
}
